<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { type FieldEntry } from 'vee-validate';
import * as rutHelpers from 'rut-helpers';
import { useQuery } from '@tanstack/vue-query';
import { representativesApi, type RepresentativeForm } from '@/api/representatives';
import { useTableFilter } from '@/composables/useTableFilter';
import RepresentativeFormFields from './representative-form-fields.vue';

const { t } = useI18n();

const nameFilter = useTableFilter(['fullName']);

interface FieldArrayMethods {
  push: (value: RepresentativeForm) => void;
  remove: (index: number) => void;
}

interface Props {
  modelValue: FieldEntry<RepresentativeForm>[];
  errors: Record<string, string | undefined>;
  fieldArrayMethods: FieldArrayMethods;
}

const props = defineProps<Props>();
const emit = defineEmits(['update:modelValue']);

const representatives = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const blankRepresentative: RepresentativeForm = {
  id: undefined,
  firstName: undefined,
  lastName: undefined,
  email: undefined,
  phoneNumber: undefined,
  dni: undefined,
  _destroy: false,
  notAssociatedYet: true,
  isEditing: true,
  observations: undefined,
  receivesCharges: false,
};

const { data: representativesList } = useQuery({
  queryKey: ['representatives'],
  queryFn: () => representativesApi.index(),
});

const selectedRepresentative = ref<number | undefined>(undefined);
const showNewRepresentativeForm = ref(false);
const newRepresentative = ref<RepresentativeForm>({ ...blankRepresentative });

function updateRepresentativeInPlace(index: number, updates: Partial<RepresentativeForm>): void {
  const representativeItem = representatives.value[index];
  if (!representativeItem) return;
  representativeItem.value = { ...representativeItem.value, ...updates };
}

function removeRepresentative(index: number): void {
  const representativeItem = representatives.value[index];
  if (!representativeItem) return;
  const representative = representativeItem.value;
  const { remove } = props.fieldArrayMethods;

  if (representative.id) {
    if (representative.notAssociatedYet) {
      remove(index);
    } else {
      updateRepresentativeInPlace(index, { _destroy: true });
    }
  } else {
    remove(index);
  }
}

function toggleEditRepresentative(index: number): void {
  const representativeItem = representatives.value[index];
  if (!representativeItem) return;
  const representative = representativeItem.value;
  updateRepresentativeInPlace(index, { isEditing: !representative.isEditing });
}

function undoRemove(index: number): void {
  updateRepresentativeInPlace(index, { _destroy: false });
}

const autocompleteKey = ref(0);
function selectExistingRepresentative(id: number): void {
  const selected = representativesList?.value?.find(
    (representative) => representative.id === id,
  );

  if (selected) {
    const { push } = props.fieldArrayMethods;
    push({
      id: selected.id,
      firstName: selected.firstName,
      lastName: selected.lastName,
      email: selected.email,
      phoneNumber: selected.phoneNumber,
      dni: selected.dni,
      _destroy: false,
      notAssociatedYet: true,
      isEditing: false,
      observations: selected.observations || undefined,
      passwordIsSet: selected.passwordIsSet,
      usesApp: selected.usesApp,
    });
  }

  nextTick(() => {
    selectedRepresentative.value = undefined;
    autocompleteKey.value += 1;
  });
}

function addNewRepresentative(): void {
  const { push } = props.fieldArrayMethods;
  push({ ...newRepresentative.value, isEditing: false });
  newRepresentative.value = { ...blankRepresentative };
  showNewRepresentativeForm.value = false;
}

watch(newRepresentative, (value) => {
  if (value.dni) {
    newRepresentative.value.dni = rutHelpers.rutFormat(value.dni);
  }
}, { deep: true });

const availableRepresentatives = computed(() => {
  const currentIds = representatives.value
    .map((representative) => representative.value.id)
    .filter((id): id is number => id !== undefined);

  return representativesList?.value?.filter(
    (representative) => !currentIds.includes(representative.id),
  ) || [];
});

watch(representatives, (value) => {
  value.forEach((representative) => {
    if (representative.value.dni) {
      representative.value.dni = rutHelpers.rutFormat(representative.value.dni);
    }

    if (representative.value.email?.trim() === '') {
      representative.value.email = undefined;
    }
  });
}, { deep: true });

function hasErrors(index: number): boolean {
  const errorKeys = Object.keys(props.errors);
  const indexPrefix = `representativesAttributes[${index}]`;

  return errorKeys.some(key => key.startsWith(indexPrefix));
}
</script>

<template>
  <v-card>
    <v-card-item>
      <v-card-title class="text-subtitle-1 font-weight-bold">
        {{ t('patient.representative') }} <span class="text-error">*</span>
      </v-card-title>
    </v-card-item>
    <v-card-text class="pt-4">
      <v-alert
        v-if="errors.representativesAttributes"
        type="info"
        variant="tonal"
        class="mt-2"
      >
        {{ errors.representativesAttributes }}
      </v-alert>
      <v-list>
        <v-list-item
          v-for="(representative, index) in representatives"
          :key="representative.key"
          :value="representative"
          :ripple="false"
          :class="{
            'error-border': !representative.value.isEditing && !representative.value._destroy && hasErrors(index),
          }"
        >
          <template v-if="representative.value._destroy === true">
            <v-list-item-title>
              Al guardar el paciente se removerá al apoderado
              {{ `${representative.value.firstName} ${representative.value.lastName}` }}.
              <v-btn
                color="primary"
                variant="outlined"
                size="small"
                @click="undoRemove(index)"
              >
                Deshacer
              </v-btn>
            </v-list-item-title>
          </template>
          <template v-else>
            <v-list-item-title class="d-flex align-center justify-space-between">
              <div>
                <span
                  v-if="representative.value.firstName && representative.value.lastName"
                >
                  {{ `${representative.value.firstName} ${representative.value.lastName}` }}
                </span>
                <span
                  v-if="representative.value.dni"
                  class="ml-2 text-caption"
                >
                  RUT: {{ representative.value.dni }}
                </span>
                <span
                  v-if="representative.value.email"
                  class="ml-2 text-caption"
                >
                  Correo: {{ representative.value.email }}
                </span>

                <v-icon
                  v-if="!representative.value.isEditing && hasErrors(index)"
                  color="error"
                  class="ml-2"
                  size="small"
                >
                  mdi-alert-circle
                </v-icon>
              </div>
              <div class="d-flex align-center">
                <v-btn
                  icon="mdi-pencil"
                  variant="text"
                  size="small"
                  class="me-2"
                  @click="toggleEditRepresentative(index)"
                />
                <v-btn
                  icon="mdi-delete"
                  color="red"
                  variant="text"
                  size="small"
                  @click="removeRepresentative(index)"
                />
              </div>
            </v-list-item-title>
          </template>
          <v-expand-transition>
            <v-card
              v-if="representative.value.isEditing"
              class="mt-4 pa-4"
              @keydown.space.stop
            >
              <representative-form-fields
                :representative-form="representative.value"
                :errors="errors"
                :error-prefix="`representativesAttributes[${index}].`"
                @update:model-value="(value) => updateRepresentativeInPlace(index, value)"
              />
              <v-row class="flex align-center pa-4">
                <v-btn
                  color="primary"
                  variant="outlined"
                  class="mt-4"
                  @click="toggleEditRepresentative(index)"
                >
                  Listo
                </v-btn>
                <p class="mt-4 ml-4 text-body-2">
                  Recuerda que debes guardar al paciente para que los cambios del apoderado se guarden.
                </p>
              </v-row>
            </v-card>
          </v-expand-transition>
        </v-list-item>
      </v-list>
      <v-divider class="my-4" />
      <p class="mb-4 text-body-2">
        Agrega al menos un apoderado. Puedes seleccionar dentro de los que ya has creado o agregar uno nuevo.
      </p>

      <v-autocomplete
        :key="`autocomplete-${autocompleteKey}`"
        v-model="selectedRepresentative"
        :items="availableRepresentatives"
        item-title="fullName"
        item-value="id"
        :custom-filter="nameFilter"
        variant="outlined"
        :label="t('patientForm.selectExistingRepresentative')"
        class="mt-4"
        no-data-text="No hay apoderados disponibles"
        :disabled="showNewRepresentativeForm"
        @update:model-value="(id: number) => id && selectExistingRepresentative(id)"
      />
      <v-btn
        v-if="!showNewRepresentativeForm"
        color="primary"
        variant="outlined"
        class="mt-4"
        @click="showNewRepresentativeForm = true"
      >
        {{ t('patientForm.addNewRepresentative') }}
      </v-btn>
      <v-expand-transition>
        <v-card
          v-if="showNewRepresentativeForm"
          class="mt-4 pa-4"
        >
          <representative-form-fields
            :representative-form="newRepresentative"
            @update:model-value="(value) => newRepresentative = value"
          />
          <div class="mt-4 d-flex justify-space-between">
            <v-btn
              color="primary"
              variant="outlined"
              @click="addNewRepresentative"
            >
              Guardar
            </v-btn>
            <v-btn
              color="error"
              variant="text"
              @click="showNewRepresentativeForm = false"
            >
              Cancelar
            </v-btn>
          </div>
        </v-card>
      </v-expand-transition>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.error-border {
  border-left: 3px solid rgb(var(--v-theme-error));
  background-color: rgb(var(--v-theme-error), .05);
}
</style>
